import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Loader2, Eye, EyeOff, CheckCircle, LogIn, User, Lock, WifiOff, AlertTriangle, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { LanguageSelector } from '../../components/ui/language-selector';
import {
  changeLanguage,
  initializeLanguage,
  type SupportedLanguage
} from '../../lib/language-service';
import { authLogger } from '@/lib/logging';
import {
  classifyLoginError,
  isOffline,
  logLoginAttempt,
  type LoginErrorClassification
} from '@/lib/login-error-classifier';

// Login request timeout (15 seconds - balances slow networks vs user patience)
const LOGIN_TIMEOUT_MS = 15000;

// Environment suffix removed - now using global environment indicator

export function Login() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [errorClassification, setErrorClassification] = useState<LoginErrorClassification | null>(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [loginStage, setLoginStage] = useState<'idle' | 'authenticating' | 'validating' | 'success'>('idle');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [shakeError, setShakeError] = useState(false);
  const [languageLoading, setLanguageLoading] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  // Track online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Initialize language on component mount
  useEffect(() => {
    // Initialize language based on cookie/URL parameters (no user/company data available on login)
    initializeLanguage();
  }, []);

  const handleLanguageChange = async (language: SupportedLanguage) => {
    if (languageLoading) return;
    
    setLanguageLoading(true);
    
    try {
      // Don't save to database on login page (user not authenticated)
      // Just update cookie and localStorage
      await changeLanguage(language, false);
      
      // Force a re-render by ensuring i18n knows about the change
      if (i18n.language !== language) {
        await i18n.changeLanguage(language);
      }
      
    } catch (_error) {
      authLogger.error('Language change failed', _error);
    } finally {
      setLanguageLoading(false);
    }
  };

  /**
   * Validate form fields with translated error messages
   * Returns true if valid, false if validation fails
   */
  const validateForm = (): boolean => {
    let isValid = true;

    // Email validation
    if (!email.trim()) {
      setEmailError(t('auth.login.emailRequired'));
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError(t('auth.login.emailInvalid'));
      isValid = false;
    } else {
      setEmailError(null);
    }

    // Password validation
    if (!password) {
      setPasswordError(t('auth.login.passwordRequired'));
      isValid = false;
    } else {
      setPasswordError(null);
    }

    return isValid;
  };

  /**
   * Helper to set error state with classification
   */
  const setLoginError = (classification: LoginErrorClassification) => {
    setErrorClassification(classification);
    // Use translated message if available, otherwise fallback
    const message = t(classification.messageKey, classification.fallbackMessage);
    setError(message);
    setLoading(false);
    setLoginStage('idle');
    setShakeError(true);
    setTimeout(() => setShakeError(false), 600);

    // Only clear password for auth errors (wrong credentials)
    if (classification.type === 'auth') {
      setPassword('');
    }
  };

  const handleLogin = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // Custom validation (replaces browser-native validation)
    if (!validateForm()) {
      return;
    }

    setError(null);
    setErrorClassification(null);
    setLoading(true);
    setLoginStage('authenticating');

    const startTime = performance.now();

    // Log attempt start
    logLoginAttempt('before', email);

    // Pre-check: Quick offline detection
    if (isOffline()) {
      const classification = classifyLoginError(new Error('Offline'));
      logLoginAttempt('after', email, { success: false, errorType: classification.type });
      setLoginError(classification);
      return;
    }

    try {
      // Create abort controller for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), LOGIN_TIMEOUT_MS);

      // Wrap the Supabase call with our timeout
      // Note: Supabase SDK doesn't support AbortSignal directly, so we race with a timeout
      const loginPromise = supabase.auth.signInWithPassword({
        email,
        password,
      });

      const timeoutPromise = new Promise<never>((_resolve, reject) => {
        controller.signal.addEventListener('abort', () => {
          reject(new Error('Login request timed out'));
        });
      });

      const { data, error: signInError } = await Promise.race([
        loginPromise,
        timeoutPromise
      ]) as Awaited<typeof loginPromise>;

      clearTimeout(timeoutId);

      if (signInError) {
        const classification = classifyLoginError(signInError);
        const latencyMs = performance.now() - startTime;
        logLoginAttempt('after', email, { success: false, errorType: classification.type, latencyMs });
        setLoginError(classification);
        return;
      }

      if (data.user && data.session) {
        // Ensure we have both user and session
        
        setLoginStage('validating');
        
        // Only validate user status for regular login (not password recovery)
        // Password recovery flows are handled by the auth context
        const urlParams = new URLSearchParams(window.location.search);
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const isPasswordRecovery = urlParams.get('type') === 'recovery' || 
                                  hashParams.get('type') === 'recovery' ||
                                  window.location.hash.includes('type=recovery') ||
                                  window.location.pathname.includes('/password-reset') ||
                                  window.location.search.includes('type=recovery');
        
        const isInvitationLogin = urlParams.get('type') === 'invite' || 
                                 hashParams.get('type') === 'invite' ||
                                 window.location.hash.includes('type=invite') ||
                                 window.location.search.includes('type=invite') ||
                                 sessionStorage.getItem('invitation_completed') === 'true';
        
        
        // ENTERPRISE APPROACH: Never call signOut after successful auth
        // Let the auth context handle user validation through proper channels
        // Successful Supabase authentication means the user credentials are valid

        if (!isPasswordRecovery && !isInvitationLogin) {
          // Standard login flow - show success and navigate
          authLogger.debug('Standard login flow completed successfully');

          // ENTERPRISE FIX: Track login time for navigation protection
          sessionStorage.setItem('loginTime', Date.now().toString());

          setLoginStage('success');
          setIsSuccess(true);
          setSuccessMessage(t('auth.login.successRedirectDashboard'));
          setLoading(false);

          // Use setTimeout to show success state before navigation
          setTimeout(() => {
            navigate('/projects');
          }, 1500);

          // Optional: Validate user status asynchronously (no blocking, no signOut)
          // This is for telemetry and monitoring only
          try {
            const response = await fetch(`${import.meta.env['VITE_PUBLIC_SUPABASE_URL']}/functions/v1/validate-user-login`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${data.session.access_token}`,
              },
              body: JSON.stringify({ user_id: data.user.id })
            });

            if (response.ok) {
              const result = await response.json();
              if (!result.valid) {
                // Log validation issues but don't disrupt the user experience
                authLogger.warn('User validation failed post-login', {
                  userId: data.user.id,
                  email: data.user.email,
                  validationError: result.error
                });
                // Note: We do NOT call signOut here - let auth context handle this
              }
            }
          } catch (validationError) {
            // Log validation errors but don't disrupt user experience
            authLogger.warn('Post-login validation check failed', validationError);
          }
        } else if (isPasswordRecovery || isInvitationLogin) {
          // Password recovery or invitation flow - activate pending users automatically
          
          try {
            // Call activation function for users completing password reset
            const activationResponse = await fetch(`${import.meta.env['VITE_PUBLIC_SUPABASE_URL']}/functions/v1/activate-user-first-login`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${data.session.access_token}`,
              },
              body: JSON.stringify({ user_id: data.user.id })
            });

            if (activationResponse.ok) {
              const activationResult = await activationResponse.json();
              if (activationResult.activated) {
                // User successfully activated - no additional action needed
              } else {
                // User activation failed - allowing login anyway
              }
            } else {
              authLogger.warn('User activation request failed, but allowing login');
            }
          } catch (activationError) {
            authLogger.error('Error during user activation', activationError);
            authLogger.warn('Activation failed, but allowing password recovery login');
          }
          
          // Allow login after activation attempt
          // ENTERPRISE FIX: Track login time for navigation protection
          sessionStorage.setItem('loginTime', Date.now().toString());

          setLoginStage('success');
          setIsSuccess(true);
          setSuccessMessage(t('auth.login.passwordResetWelcome'));
          setLoading(false);
          // Use setTimeout to show success state before navigation
          setTimeout(() => {
            navigate('/projects');
          }, 1500);
        }
      } else {
        // Missing user or session data
        authLogger.error('Login error: Missing user or session data');
        const classification: LoginErrorClassification = {
          type: 'unknown',
          isRetryable: true,
          messageKey: 'auth.login.unknownError',
          fallbackMessage: 'Login failed. Please try again.',
          suggestedAction: 'retry',
          technicalMessage: 'Missing user or session data after successful auth'
        };
        logLoginAttempt('after', email, { success: false, errorType: 'unknown', latencyMs: performance.now() - startTime });
        setLoginError(classification);
      }
    } catch (caughtError) {
      const latencyMs = performance.now() - startTime;
      authLogger.error('Login failed', caughtError);

      const classification = classifyLoginError(caughtError);
      logLoginAttempt('after', email, { success: false, errorType: classification.type, latencyMs });
      setLoginError(classification);
    }
  };

  return (
    <div 
      className='flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4 transition-colors duration-500' 
      style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '100vh',
        width: '100%',
        position: 'fixed',
        top: 0,
        left: 0
      }}
      data-testid="login-page"
    >
      <div className='w-full max-w-md'>
        <Card className={`shadow-xl border-0 bg-white/80 backdrop-blur-sm transition-all duration-300 hover:shadow-2xl ${shakeError ? 'animate-shake' : ''}`} data-testid="login-card">
          <CardHeader className='text-center pb-8'>
            <div className='flex justify-end mb-4'>
              <LanguageSelector
                value={i18n.language as SupportedLanguage}
                onLanguageChange={handleLanguageChange}
                disabled={languageLoading || loading}
                variant="select"
                size="sm"
                showIcon={true}
                showLabel={false}
              />
            </div>
            <CardTitle className='text-3xl font-bold text-slate-900' data-testid="login-title">
              {t('auth.login.title')}
            </CardTitle>
            <CardDescription className='text-slate-600 mt-2' data-testid="login-description">
              {isSuccess
                ? t('auth.login.successRedirect')
                : loginStage === 'authenticating'
                ? t('auth.login.verifyingCredentials')
                : loginStage === 'validating'
                ? t('auth.login.settingUpSession')
                : t('auth.login.enterCredentials')}
            </CardDescription>
          </CardHeader>
          <CardContent className='pb-8'>
            {isSuccess ? (
              <div className='space-y-6'>
                <div className='p-4 bg-green-50 border border-green-200 rounded-lg' role="status" aria-live="polite">
                  <div className='flex items-center'>
                    <CheckCircle className='h-6 w-6 text-green-600 mr-3' aria-hidden="true" />
                    <div>
                      <p className='text-sm font-medium text-green-800'>{t('auth.login.loginSuccess')}</p>
                      <p className='text-sm text-green-700 mt-1'>{successMessage}</p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <form onSubmit={handleLogin} className='space-y-6' data-testid="login-form" role="form" aria-label="Login form" noValidate>
              <div className='space-y-2'>
                <Label htmlFor='email' className='text-sm font-medium text-slate-700 flex items-center'>
                  <User className='mr-2 h-4 w-4 text-slate-500' />
                  {t('auth.login.emailLabel')}
                </Label>
                <Input
                  id='email'
                  type='text'
                  placeholder='you@example.com'
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (emailError) setEmailError(null);
                  }}
                  disabled={loading}
                  className={`h-12 transition-all duration-200 focus:ring-2 focus:ring-primary/20 border-slate-200 hover:border-primary/40 ${emailError ? 'border-red-300 focus:border-red-500 focus:ring-red-200' : ''}`}
                  data-testid="email-input"
                  aria-describedby={emailError ? 'email-validation-error' : undefined}
                  aria-invalid={!!emailError}
                  autoComplete='email'
                  autoCapitalize='none'
                />
                {emailError && (
                  <p className='text-sm text-red-600 mt-1' id='email-validation-error' role='alert'>
                    {emailError}
                  </p>
                )}
              </div>
              <div className='space-y-2'>
                <Label htmlFor='password' className='text-sm font-medium text-slate-700 flex items-center'>
                  <Lock className='mr-2 h-4 w-4 text-slate-500' />
                  {t('auth.login.passwordLabel')}
                </Label>
                <div className='relative'>
                  <Input
                    id='password'
                    type={showPassword ? 'text' : 'password'}
                    placeholder='••••••••'
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (passwordError) setPasswordError(null);
                    }}
                    disabled={loading}
                    className={`h-12 pr-12 transition-all duration-200 focus:ring-2 focus:ring-primary/20 border-slate-200 hover:border-primary/40 ${passwordError ? 'border-red-300 focus:border-red-500 focus:ring-red-200' : ''}`}
                    data-testid="password-input"
                    aria-describedby={passwordError ? 'password-validation-error' : 'password-visibility-toggle'}
                    aria-invalid={!!passwordError}
                    autoComplete='current-password'
                  />
                  <button
                    type='button'
                    onClick={() => setShowPassword(!showPassword)}
                    className='absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors'
                    disabled={loading}
                    aria-label={showPassword ? t('auth.login.hidePassword') : t('auth.login.showPassword')}
                    id='password-visibility-toggle'
                    tabIndex={0}
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                {passwordError && (
                  <p className='text-sm text-red-600 mt-1' id='password-validation-error' role='alert'>
                    {passwordError}
                  </p>
                )}
              </div>
              {/* Offline warning banner */}
              {!isOnline && (
                <div className='p-3 bg-amber-50 border border-amber-200 rounded-lg' role="status" aria-live="polite">
                  <div className='flex items-center'>
                    <WifiOff className='h-5 w-5 text-amber-600 mr-2 flex-shrink-0' />
                    <p className='text-sm text-amber-700'>{t('auth.login.offlineWarning')}</p>
                  </div>
                </div>
              )}

              {/* Error display with color-coding by error type */}
              {error && errorClassification && (
                <div
                  className={`p-3 rounded-lg ${
                    errorClassification.type === 'network' || errorClassification.type === 'timeout'
                      ? 'bg-amber-50 border border-amber-200'  // Yellow for network/timeout
                      : errorClassification.type === 'server'
                      ? 'bg-blue-50 border border-blue-200'    // Blue for server errors
                      : 'bg-red-50 border border-red-200'      // Red for auth errors
                  }`}
                  data-testid="login-error"
                  role="alert"
                  aria-live="polite"
                >
                  <div className='flex items-start'>
                    {/* Icon based on error type */}
                    {(errorClassification.type === 'network' || errorClassification.type === 'timeout') && (
                      <WifiOff className='h-5 w-5 text-amber-600 mr-2 flex-shrink-0 mt-0.5' />
                    )}
                    {errorClassification.type === 'server' && (
                      <AlertTriangle className='h-5 w-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5' />
                    )}
                    {errorClassification.type === 'auth' && (
                      <Lock className='h-5 w-5 text-red-600 mr-2 flex-shrink-0 mt-0.5' />
                    )}
                    {errorClassification.type === 'unknown' && (
                      <AlertTriangle className='h-5 w-5 text-red-600 mr-2 flex-shrink-0 mt-0.5' />
                    )}
                    <div className='flex-1'>
                      <p className={`text-sm font-medium ${
                        errorClassification.type === 'network' || errorClassification.type === 'timeout'
                          ? 'text-amber-800'
                          : errorClassification.type === 'server'
                          ? 'text-blue-800'
                          : 'text-red-800'
                      }`} id='login-error-message'>
                        {error}
                      </p>

                      {/* Auth error specific hints */}
                      {errorClassification.type === 'auth' && (
                        <p className='mt-1 text-xs text-red-600'>
                          {t('auth.login.troubleLoggingIn')}
                        </p>
                      )}

                      {/* Server error hint */}
                      {errorClassification.type === 'server' && (
                        <p className='mt-1 text-xs text-blue-600'>
                          {t('auth.login.serverErrorHint')}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Action buttons based on error type */}
                  <div className='mt-3 flex items-center justify-center gap-3'>
                    {/* Retry button for network/server/timeout errors */}
                    {errorClassification.isRetryable && (
                      <button
                        type='button'
                        onClick={() => {
                          setError(null);
                          setErrorClassification(null);
                          setLoginStage('idle');
                        }}
                        className={`inline-flex items-center text-xs font-medium transition-colors duration-200 ${
                          errorClassification.type === 'network' || errorClassification.type === 'timeout'
                            ? 'text-amber-700 hover:text-amber-900'
                            : errorClassification.type === 'server'
                            ? 'text-blue-700 hover:text-blue-900'
                            : 'text-red-600 hover:text-red-800'
                        }`}
                      >
                        <RefreshCw className='h-3 w-3 mr-1' />
                        {t('auth.login.retryButton')}
                      </button>
                    )}

                    {/* Forgot password link for auth errors */}
                    {errorClassification.suggestedAction === 'check_credentials' && (
                      <Link
                        to='/request-password-reset'
                        className='text-xs font-medium text-primary hover:text-primary/80 transition-colors duration-200'
                      >
                        {t('auth.login.forgotPassword')}
                      </Link>
                    )}

                    {/* Contact support for account issues */}
                    {errorClassification.suggestedAction === 'contact_support' && (
                      <span className='text-xs text-red-600'>
                        {t('auth.login.contactSupport')}
                      </span>
                    )}
                  </div>
                </div>
              )}
              <Button 
                type='submit' 
                className='w-full h-12 text-base font-medium transition-all duration-200 hover:shadow-lg hover:scale-[1.02] active:scale-[0.98]' 
                disabled={loading} 
                data-testid="login-button"
                aria-describedby={error ? 'login-error-message' : undefined}
              >
                {loading ? (
                  <div className='flex items-center justify-center'>
                    <Loader2 className='mr-2 h-5 w-5 animate-spin' />
                    {loginStage === 'authenticating' ? t('auth.login.authenticating') :
                     loginStage === 'validating' ? t('auth.login.validating') : t('auth.login.loggingIn')}
                  </div>
                ) : (
                  <div className='flex items-center justify-center'>
                    <LogIn className='mr-2 h-5 w-5' />
                    {t('auth.login.logInButton')}
                  </div>
                )}
              </Button>
              </form>
            )}
            {!isSuccess && (
              <div className='mt-8 space-y-4 text-center'>
                <div className='relative'>
                  <div className='absolute inset-0 flex items-center'>
                    <div className='w-full border-t border-slate-200' />
                  </div>
                  <div className='relative flex justify-center text-sm'>
                    <span className='px-4 bg-white text-slate-500'>{t('auth.login.needHelp')}</span>
                  </div>
                </div>
                <div className='space-y-3'>
                  <p className='text-sm text-slate-600'>
                    <Link 
                      to='/request-password-reset' 
                      className='font-medium text-primary hover:text-primary/80 transition-colors duration-200' 
                      data-testid="forgot-password-link"
                    >
                      {t('auth.login.forgotPassword')}
                    </Link>
                  </p>
                </div>
                {/* Show help tips only for auth errors (wrong credentials) */}
                {error && errorClassification?.type === 'auth' && (
                  <div className='mt-6 p-3 bg-blue-50 border border-blue-200 rounded-lg'>
                    <p className='text-xs text-blue-700'>
                      💡 <strong>{t('auth.login.helpNeeded')}</strong> {t('auth.login.commonIssues')}
                    </p>
                    <ul className='text-xs text-blue-600 mt-1 list-disc list-inside'>
                      <li>{t('auth.login.checkEmailSpelling')}</li>
                      <li>{t('auth.login.ensureCapsLockOff')}</li>
                      <li>{t('auth.login.tryResetPassword')}</li>
                    </ul>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}